import { useEffect, useMemo, useRef, useState, type ElementType, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Drawer, Box, Typography, TextField, Button, Stack, MenuItem, Divider,
  Grid, FormControlLabel, Switch, Alert, IconButton, InputAdornment,
  Autocomplete, Chip, LinearProgress, alpha,
} from '@mui/material';
import {
  Close, ArrowBack, Visibility, VisibilityOff, Person, DirectionsCar, DeliveryDining,
  Storefront, Handyman, Event, Badge, AdminPanelSettings,
} from '@mui/icons-material';
import GeoMasterSelect from '../masters/GeoMasterSelect';
import FormFileUpload from '../ui/forms/FormFileUpload';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneWithDialCodeField from '../ui/forms/PhoneWithDialCodeField';
import AddressLookupField from '../ui/forms/AddressLookupField';
import PartnerReviewDetail from '../documents/PartnerReviewDetail';
import type { PartnerRow } from '../../services/partnerUserService';
import { SERVICE_HUB_OPTIONS } from '../../constants/partnerServices';
import {
  USER_TYPE_FORM_OPTIONS,
  USER_TYPE_LABELS,
  USER_TYPE_ROLE_HINTS,
  GENDER_OPTIONS,
  PARTNER_USER_TYPES,
  STAFF_USER_TYPES,
  PARTNER_ENTITY_BY_USER_TYPE,
  PREFERRED_LANGUAGE_OPTIONS,
  getFormTabsForUserType,
  getUserTypeFormConfig,
  emptyUserForm,
  userRowToForm,
  validateUserForm,
  maxDateOfBirth,
  minAgeForUserType,
  resolveBlockedReason,
  isMobileAppUserType,
  type UserFormState,
  type UserRow,
} from '../../constants/userManagement';
import { getUserById, listAdminRoles } from '../../services/userService';
import { listCountryDialCodes } from '../../services/countryDialCodeService';

interface ZoneOption { _id: string; name: string; code?: string }

const ROLE_ICONS: Record<string, ElementType> = {
  customer: Person,
  driver: DirectionsCar,
  delivery_partner: DeliveryDining,
  merchant: Storefront,
  service_provider: Handyman,
  vendor: Event,
  employee: Badge,
  client_admin: AdminPanelSettings,
};

const ROLE_SHORT_LABELS: Record<string, string> = {
  customer: 'Customer',
  driver: 'Driver',
  delivery_partner: 'Delivery',
  merchant: 'Merchant',
  service_provider: 'Service Pro',
  vendor: 'Vendor',
  employee: 'Employee',
  client_admin: 'Admin',
};

const ROLE_COLORS: Record<string, string> = {
  customer: '#64748b',
  driver: '#2563eb',
  delivery_partner: '#ea580c',
  merchant: '#059669',
  service_provider: '#7c3aed',
  vendor: '#db2777',
  employee: '#0891b2',
  client_admin: '#6366f1',
};

interface UserFormDrawerProps {
  open: boolean;
  onClose: () => void;
  editingUser: UserRow | null;
  zones: ZoneOption[];
  loading?: boolean;
  saveError?: string;
  onSave: (form: UserFormState) => void;
  defaultUserType?: string;
  defaultServiceHub?: string;
  lockServiceHub?: boolean;
  allowedUserTypes?: string[];
}

export default function UserFormDrawer({
  open,
  onClose,
  editingUser,
  zones,
  loading,
  saveError,
  onSave,
  defaultUserType,
  defaultServiceHub,
  lockServiceHub,
  allowedUserTypes,
}: UserFormDrawerProps) {
  const isEdit = !!editingUser;
  const [form, setForm] = useState<UserFormState>(emptyUserForm());
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Create: pick role first, then open role-specific form on the next step. */
  const [step, setStep] = useState<'type' | 'form'>('type');
  const bodyRef = useRef<HTMLDivElement>(null);

  const { data: fullUser, isLoading: loadingUser, isError: loadUserError } = useQuery({
    queryKey: ['admin-user', editingUser?._id],
    queryFn: () => getUserById(editingUser!._id),
    enabled: open && !!editingUser?._id,
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['admin-roles-mini'],
    queryFn: listAdminRoles,
    enabled: open,
    staleTime: 60_000,
  });

  useQuery({
    queryKey: ['country-dial-codes'],
    queryFn: listCountryDialCodes,
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const userTypeOptions = useMemo(() => {
    if (!allowedUserTypes?.length) return USER_TYPE_FORM_OPTIONS;
    return USER_TYPE_FORM_OPTIONS.filter((o) => allowedUserTypes.includes(o.value));
  }, [allowedUserTypes]);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setShowPassword(false);
    const singleType = allowedUserTypes?.length === 1 ? allowedUserTypes[0] : null;
    if (editingUser) {
      setForm(userRowToForm(fullUser || editingUser));
      setStep('form');
    } else {
      const userType = singleType || defaultUserType || '';
      const cfg = getUserTypeFormConfig(userType || 'customer');
      setForm(emptyUserForm({
        userType,
        serviceHub: defaultServiceHub || cfg.defaultServiceHub || '',
        partnerEntityType: PARTNER_ENTITY_BY_USER_TYPE[userType] || '',
      }));
      // Multiple roles → role picker first; single/default type → go straight to form
      setStep(userType && (singleType || defaultUserType) ? 'form' : (userTypeOptions.length <= 1 ? 'form' : 'type'));
    }
  }, [open, editingUser, fullUser, defaultUserType, defaultServiceHub, allowedUserTypes, userTypeOptions]);

  const tabs = useMemo(
    () => getFormTabsForUserType(form.userType),
    [form.userType],
  );
  const typeConfig = getUserTypeFormConfig(form.userType);
  const canChangeRole = !isEdit && userTypeOptions.length > 1;
  const showTypeStep = !isEdit && step === 'type' && canChangeRole;
  const showFormStep = isEdit || step === 'form';

  const section = (title: string, children: ReactNode, subtitle?: string) => (
    <Box key={title} sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.25 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
          {subtitle}
        </Typography>
      )}
      {children}
      <Divider sx={{ mt: 2.5 }} />
    </Box>
  );

  const patch = (p: Partial<UserFormState>) => setForm((f) => ({ ...f, ...p }));

  const onUserTypeChange = (userType: string) => {
    const cfg = getUserTypeFormConfig(userType);
    patch({
      userType,
      partnerEntityType: PARTNER_ENTITY_BY_USER_TYPE[userType] || '',
      serviceHub: lockServiceHub
        ? (defaultServiceHub || form.serviceHub)
        : (cfg.defaultServiceHub || form.serviceHub),
      roleIds: STAFF_USER_TYPES.has(userType) ? form.roleIds : [],
      zoneId: cfg.requiresZone ? form.zoneId : '',
      zoneIds: STAFF_USER_TYPES.has(userType) ? form.zoneIds : [],
      ...(cfg.mobileLoginOnly ? { password: '', confirmPassword: '' } : {}),
    });
    setErrors({});
  };

  /** Select role and move to that role’s form (next step). */
  const goToRoleForm = (userType: string) => {
    onUserTypeChange(userType);
    setStep('form');
    bodyRef.current?.scrollTo?.({ top: 0 });
  };

  const goBackToTypes = () => {
    if (!canChangeRole) {
      onClose();
      return;
    }
    setErrors({});
    setStep('type');
  };

  const continueFromTypeStep = () => {
    if (!form.userType) {
      setErrors({ userType: 'Select an account type to continue' });
      return;
    }
    setStep('form');
    bodyRef.current?.scrollTo?.({ top: 0 });
  };

  const isPartner = PARTNER_USER_TYPES.has(form.userType);
  const isStaff = STAFF_USER_TYPES.has(form.userType);
  const mobileLoginOnly = isMobileAppUserType(form.userType);
  const selectedStaffZones = useMemo(
    () => zones.filter((z) => (form.zoneIds || []).includes(z._id)),
    [zones, form.zoneIds],
  );
  const selectedRoles = useMemo(
    () => roles.filter((r) => (form.roleIds || []).includes(r._id)),
    [roles, form.roleIds],
  );

  const saveForm = () => {
    const finalErrors = validateUserForm(form, isEdit);
    if (Object.keys(finalErrors).length) {
      setErrors(finalErrors);
      requestAnimationFrame(() => {
        const invalid = bodyRef.current?.querySelector('[aria-invalid="true"]') as HTMLElement | null;
        invalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    onSave(form);
  };

  const renderProfileTab = () => (
    <Grid container spacing={2}>
      {mobileLoginOnly && (
        <Grid item xs={12}>
          <Alert severity="info" variant="outlined">
            This account signs in on the mobile app with phone OTP — no web password.
          </Alert>
        </Grid>
      )}
      {isStaff && (
        <Grid item xs={12}>
          <Alert severity="info" variant="outlined">
            Employee and admin accounts sign in on the web admin panel with email and password.
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <PersonNameField fullWidth size="small" required label="First Name" value={form.firstName}
          error={!!errors.firstName} helperText={errors.firstName || 'Letters only — no numbers or special characters'}
          onChange={(e) => patch({ firstName: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <PersonNameField fullWidth size="small" label="Last Name" value={form.lastName}
          error={!!errors.lastName} helperText={errors.lastName || 'Letters only — no numbers or special characters'}
          onChange={(e) => patch({ lastName: e.target.value })} />
      </Grid>
      <Grid item xs={12}>
        <PhoneWithDialCodeField
          label="Phone Number"
          phone={form.phone}
          countryCode={form.phoneCountryCode}
          preferredCountry={form.country}
          error={!!errors.phone || !!errors.contact}
          helperText={errors.phone || errors.contact || (mobileLoginOnly ? 'Required — used for mobile OTP login' : 'Optional for staff')}
          required={mobileLoginOnly || isPartner}
          onChange={({ phone, countryCode }) => patch({ phone, phoneCountryCode: countryCode })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          type="email"
          required={isStaff}
          label="Email"
          value={form.email}
          error={!!errors.email}
          helperText={errors.email || (isStaff ? 'Required — admin web login' : 'Optional')}
          onChange={(e) => patch({ email: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" select label="Gender" value={form.gender}
          onChange={(e) => patch({ gender: e.target.value })}>
          {GENDER_OPTIONS.map((g) => (
            <MenuItem key={g.value || 'na'} value={g.value}>{g.label}</MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="Date of Birth"
          required={isPartner}
          value={form.dateOfBirth}
          error={!!errors.dateOfBirth}
          helperText={errors.dateOfBirth || `Must be at least ${minAgeForUserType(form.userType)} years old`}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: maxDateOfBirth(form.userType) }}
          onChange={(e) => patch({ dateOfBirth: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <GeoMasterSelect masterType="countries" label="Country" value={form.country}
          onChange={(code) => patch({ country: code })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" select label="Preferred Language" value={form.language}
          onChange={(e) => patch({ language: e.target.value })}>
          <MenuItem value="">Select language</MenuItem>
          {PREFERRED_LANGUAGE_OPTIONS.map((lang) => (
            <MenuItem key={lang.code} value={lang.code}>
              {lang.name}{lang.nativeName !== lang.name ? ` · ${lang.nativeName}` : ''}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <PhoneWithDialCodeField
          label="Emergency Contact"
          phone={form.emergencyPhone}
          countryCode={form.emergencyPhoneCountryCode}
          preferredCountry={form.country}
          error={!!errors.emergencyPhone}
          helperText={errors.emergencyPhone}
          onChange={({ phone, countryCode }) => patch({ emergencyPhone: phone, emergencyPhoneCountryCode: countryCode })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormFileUpload label="Profile Photo" value={form.avatar}
          onChange={(url) => patch({ avatar: url })} folder="users/avatars" />
      </Grid>
    </Grid>
  );

  const renderAccountTypePicker = () => {
    const groups = [...new Set(userTypeOptions.map((o) => o.group))];

    return (
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Choose the account type. The next step shows only the fields for that role.
        </Typography>
        {errors.userType && (
          <Alert severity="error" sx={{ fontWeight: 700 }}>{errors.userType}</Alert>
        )}
        {groups.map((group, groupIndex) => (
          <Box key={group}>
            {groupIndex > 0 && <Divider sx={{ mb: 1.5 }} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: 'block', fontWeight: 700, letterSpacing: '0.04em' }}
            >
              {group.toUpperCase()}
            </Typography>
            <Grid container spacing={1}>
              {userTypeOptions.filter((o) => o.group === group).map((opt) => {
                const Icon = ROLE_ICONS[opt.value] || Person;
                const color = ROLE_COLORS[opt.value] || '#64748b';
                const selected = form.userType === opt.value;
                const hint = USER_TYPE_ROLE_HINTS[opt.value];
                return (
                  <Grid item xs={12} sm={6} key={opt.value}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => goToRoleForm(opt.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          goToRoleForm(opt.value);
                        }
                      }}
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 2,
                        border: '1.5px solid',
                        borderColor: selected ? color : 'divider',
                        bgcolor: selected ? alpha(color, 0.07) : 'background.paper',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.25,
                        minHeight: 72,
                        boxShadow: selected ? `0 0 0 1px ${alpha(color, 0.25)}` : 'none',
                        transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
                        '&:hover': {
                          borderColor: color,
                          bgcolor: alpha(color, 0.04),
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${alpha(color, 0.45)}`,
                          outlineOffset: 1,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(color, 0.1),
                          color,
                          flexShrink: 0,
                          mt: 0.25,
                        }}
                      >
                        <Icon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box minWidth={0}>
                        <Typography variant="body2" fontWeight={700}>
                          {ROLE_SHORT_LABELS[opt.value] || opt.label}
                        </Typography>
                        {hint && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>
                            {hint.steps}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
      </Stack>
    );
  };

  const renderSelectedRoleBanner = () => {
    if (!form.userType || showTypeStep) return null;
    const color = ROLE_COLORS[form.userType] || '#64748b';
    const Icon = ROLE_ICONS[form.userType] || Person;
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          mb: 2,
          px: 1.25,
          py: 0.75,
          borderRadius: 2,
          border: '1px solid',
          borderColor: alpha(color, 0.2),
          bgcolor: alpha(color, 0.04),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(color, 0.12),
              color,
            }}
          >
            <Icon sx={{ fontSize: 16 }} />
          </Box>
          <Box minWidth={0}>
            <Typography variant="body2" fontWeight={650} noWrap>
              {USER_TYPE_LABELS[form.userType] || form.userType}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {USER_TYPE_ROLE_HINTS[form.userType]?.steps}
            </Typography>
          </Box>
        </Stack>
        {canChangeRole && (
          <Button size="small" onClick={goBackToTypes}>Change type</Button>
        )}
      </Stack>
    );
  };

  const renderAssignmentTab = () => (
    <Grid container spacing={2}>
      {isStaff && (
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={zones}
            value={selectedStaffZones}
            getOptionLabel={(z) => `${z.name}${z.code ? ` (${z.code})` : ''}`}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            onChange={(_, value) => patch({
              zoneIds: value.map((z) => z._id),
              zoneId: value[0]?._id || '',
            })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option._id} label={option.code || option.name} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Operating zones"
                helperText="Assign one or more zones for city/zone admin staff. They manage partners, drivers, and ops only in these zones."
              />
            )}
          />
        </Grid>
      )}
      {(isPartner || typeConfig.requiresServiceHub) && (
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" select label="Service Hub" value={form.serviceHub}
          disabled={lockServiceHub}
          error={!!errors.serviceHub} helperText={errors.serviceHub || (typeConfig.requiresServiceHub ? 'Required for partners' : '')}
          onChange={(e) => patch({ serviceHub: e.target.value })}>
          <MenuItem value="">None</MenuItem>
          {SERVICE_HUB_OPTIONS.map((h) => (
            <MenuItem key={h.value} value={h.value}>{h.label}</MenuItem>
          ))}
        </TextField>
      </Grid>
      )}
      {(isPartner || typeConfig.requiresZone) && (
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" select label="Operating Zone" value={form.zoneId}
          error={!!errors.zoneId} helperText={errors.zoneId || (typeConfig.requiresZone ? 'Required for partners' : '')}
          onChange={(e) => patch({ zoneId: e.target.value, zoneIds: e.target.value ? [e.target.value] : [] })}>
          <MenuItem value="">No zone</MenuItem>
          {zones.map((z) => (
            <MenuItem key={z._id} value={z._id}>{z.name}{z.code ? ` (${z.code})` : ''}</MenuItem>
          ))}
        </TextField>
      </Grid>
      )}
      <Grid item xs={12}>
        <Alert severity="info" variant="outlined">
          {isStaff
            ? 'Tenant admins see all zones. City admins with assigned zones manage partners, drivers, dispatch, and onboarding only in those zones.'
            : 'Partners need a zone and hub before they can receive jobs.'}
        </Alert>
      </Grid>
    </Grid>
  );

  const renderPartnerTab = () => (
    <Grid container spacing={2}>
      {(form.userType === 'service_provider' || form.userType === 'merchant' || form.userType === 'vendor') && (
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            required
            label={form.userType === 'service_provider' ? 'Service / Skill name' : 'Business / Store Name'}
            value={form.businessName}
            error={!!errors.businessName}
            helperText={errors.businessName || (form.userType === 'service_provider' ? 'Shown on the provider app profile' : 'As shown in the partner app')}
            onChange={(e) => patch({ businessName: e.target.value })}
          />
        </Grid>
      )}
      {(form.userType === 'driver' || form.userType === 'delivery_partner') && (
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Vehicle number"
            value={form.vehicleNumber}
            helperText="Same as partner app vehicle / plate number"
            onChange={(e) => patch({ vehicleNumber: e.target.value })}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Referral code" value={form.referralCode}
          helperText="Optional invite / referral code"
          onChange={(e) => patch({ referralCode: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" type="number" label="Commission %" value={form.commissionPercent}
          inputProps={{ min: 0, max: 100, step: 0.5 }}
          helperText="Partner commission override (optional)"
          onChange={(e) => patch({ commissionPercent: e.target.value })} />
      </Grid>
      {!isEdit && (
        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={form.autoVerify} onChange={(e) => patch({ autoVerify: e.target.checked })} />}
            label="Auto-verify partner (skip document approval queue)"
          />
        </Grid>
      )}
    </Grid>
  );

  const renderDocumentsTab = () => {
    const entityType = form.partnerEntityType || PARTNER_ENTITY_BY_USER_TYPE[form.userType] || 'driver';
    const source = fullUser || editingUser;
    if (!isEdit || !source?._id) {
      return (
        <Alert severity="info">
          Create this driver first, then open Edit User → Documents to upload, approve, or reject KYC.
          Approved documents are required before they can use the driver app.
        </Alert>
      );
    }
    const zoneRef = source.zoneId;
    const zoneId = form.zoneId || (typeof zoneRef === 'object' ? zoneRef?._id : zoneRef);
    const partner: PartnerRow = {
      _id: source._id,
      firstName: form.firstName || source.firstName || '',
      lastName: form.lastName || source.lastName,
      email: form.email || source.email,
      phone: form.phone || source.phone,
      userType: form.userType || source.userType || 'driver',
      partnerEntityType: entityType,
      isActive: source.isActive !== false,
      isVerified: !!source.isVerified,
      isBlocked: !!source.isBlocked,
      partnerStatus: source.partnerStatus || 'pending_approval',
      zoneId: typeof zoneRef === 'object' && zoneRef
        ? { _id: zoneRef._id, name: zoneRef.name || '', code: zoneRef.code }
        : zoneId
          ? { _id: zoneId, name: '' }
          : undefined,
      serviceHub: form.serviceHub || source.serviceHub,
    };
    return (
      <Stack spacing={1.5}>
        <Alert severity="info" variant="outlined">
          Approve required documents here. The driver can use the driver app only after verification is complete.
        </Alert>
        <PartnerReviewDetail partner={partner} entityType={entityType} embedded />
      </Stack>
    );
  };

  const renderAccessTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          options={roles}
          value={selectedRoles}
          getOptionLabel={(opt) => opt.name || opt.code || opt._id}
          isOptionEqualToValue={(a, b) => a._id === b._id}
          onChange={(_, value) => patch({ roleIds: value.map((r) => r._id) })}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option._id} label={option.name} size="small" />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} size="small" label="Assigned Roles"
              required={isStaff}
              error={!!errors.roleIds}
              placeholder="Select roles for dashboard access…"
              helperText={errors.roleIds || (typeConfig.suggestRoles ? 'Required for staff and admin accounts' : '')} />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Employee ID" value={form.employeeId}
          onChange={(e) => patch({ employeeId: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Department" value={form.department}
          placeholder="e.g. Operations, Dispatch"
          onChange={(e) => patch({ department: e.target.value })} />
      </Grid>
    </Grid>
  );

  const renderSecurityTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert severity="info" variant="outlined">
          Web admin login only. Customers, drivers, and providers use mobile OTP — they do not need a password here.
        </Alert>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth size="small"
          type={showPassword ? 'text' : 'password'}
          label={isEdit ? 'New Password (optional)' : 'Web password'}
          value={form.password}
          error={!!errors.password}
          helperText={errors.password || (isEdit ? 'Leave blank to keep current' : 'Min 6 characters — admin panel login')}
          placeholder={isEdit ? '' : 'Welcome@2024'}
          onChange={(e) => patch({ password: e.target.value })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth size="small"
          type={showPassword ? 'text' : 'password'}
          label="Confirm password"
          value={form.confirmPassword}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          onChange={(e) => patch({ confirmPassword: e.target.value })}
        />
      </Grid>
      {!isEdit && (
        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={form.sendWelcomeEmail} onChange={(e) => patch({ sendWelcomeEmail: e.target.checked })} />}
            label="Send welcome email with login details"
          />
        </Grid>
      )}
    </Grid>
  );

  const renderNotesTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <AddressLookupField
          label="Street Address"
          value={form.address}
          country={form.country}
          onAddressChange={(address) => patch({ address })}
          onSelect={(loc) => {
            patch({
              address: loc.address || form.address,
              city: loc.city || form.city,
              state: loc.state || form.state,
              postalCode: loc.postalCode || form.postalCode,
              country: loc.countryCode || form.country,
            });
          }}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="City" value={form.city}
          onChange={(e) => patch({ city: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="State / Region" value={form.state}
          onChange={(e) => patch({ state: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="Postal / PIN Code" value={form.postalCode}
          onChange={(e) => patch({ postalCode: e.target.value })} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" multiline minRows={3} label="Internal Admin Notes"
          value={form.notes} onChange={(e) => patch({ notes: e.target.value })} />
      </Grid>
    </Grid>
  );

  if (!open) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: (showFormStep && tabs.includes('documents')) ? 880 : 640 } } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          {showFormStep && canChangeRole && (
            <IconButton size="small" onClick={goBackToTypes} sx={{ mt: 0.25 }} aria-label="Back to roles">
              <ArrowBack />
            </IconButton>
          )}
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" fontWeight={800}>
              {isEdit
                ? 'Edit User'
                : showTypeStep
                  ? 'Choose account type'
                  : `Add ${USER_TYPE_LABELS[form.userType] || 'User'}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit
                ? `${form.firstName} ${form.lastName}`.trim() || editingUser?.email || 'Update account'
                : showTypeStep
                  ? 'Step 1 of 2 — pick a role, then continue to its form'
                  : USER_TYPE_ROLE_HINTS[form.userType]?.summary || 'Step 2 of 2 — fill role fields'}
            </Typography>
            {!isEdit && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {showTypeStep ? 'Step 1 of 2 · Account type' : 'Step 2 of 2 · Details'}
              </Typography>
            )}
            {isEdit && (fullUser?.createdAt || editingUser?.createdAt) && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Registered {new Date(String(fullUser?.createdAt || editingUser?.createdAt)).toLocaleString()}
              </Typography>
            )}
            {isEdit && (fullUser?.isBlocked || editingUser?.isBlocked) && (
              <Alert severity="error" variant="outlined" sx={{ mt: 1, py: 0.5 }}>
                Blocked — {resolveBlockedReason(fullUser || editingUser || { isBlocked: true })}
              </Alert>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }} aria-label="Close">
            <Close />
          </IconButton>
        </Stack>

        {(loadingUser && isEdit) && <LinearProgress />}

        <Box ref={bodyRef} sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          {loadUserError && isEdit && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Could not refresh full profile from server — showing list data. You can still edit and save.
            </Alert>
          )}
          {saveError && (
            <Alert severity="error" sx={{ mb: 2, fontWeight: 700 }}>{saveError}</Alert>
          )}

          {showTypeStep && renderAccountTypePicker()}

          {showFormStep && (
            <>
              {Object.keys(errors).length > 0 && (
                <Alert severity="error" sx={{ mb: 2, fontWeight: 700 }}>
                  Please fix the highlighted required fields.
                </Alert>
              )}
              {renderSelectedRoleBanner()}
              {section(
                form.userType === 'customer' ? 'Customer profile' : 'Contact & identity',
                renderProfileTab(),
                form.userType === 'customer'
                  ? 'Matches customer app registration: name, phone, photo'
                  : isStaff
                    ? 'Staff profile for web admin login'
                    : 'Matches partner / provider app signup contact fields',
              )}
              {tabs.includes('assignment') && section(
                'Zone & service',
                renderAssignmentTab(),
                'Operating area and hub — same as mobile onboarding',
              )}
              {tabs.includes('partner') && section(
                form.userType === 'driver' || form.userType === 'delivery_partner'
                  ? 'Vehicle & partner details'
                  : 'Partner details',
                renderPartnerTab(),
              )}
              {tabs.includes('documents') && section(
                'Documents & KYC',
                renderDocumentsTab(),
                'Approve docs so the partner can go online in the app',
              )}
              {tabs.includes('access') && section('Access & roles', renderAccessTab())}
              {tabs.includes('security') && isStaff && section(
                'Login & security',
                renderSecurityTab(),
                'Web admin password — employee and admin only',
              )}
              {tabs.includes('notes') && section(
                form.userType === 'customer' ? 'Address' : 'Address & notes',
                renderNotesTab(),
              )}
            </>
          )}
        </Box>

        <Divider />
        <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ p: 2 }}>
          {showFormStep && canChangeRole ? (
            <Button startIcon={<ArrowBack />} onClick={goBackToTypes}>Back</Button>
          ) : (
            <Button onClick={onClose}>Cancel</Button>
          )}
          {showTypeStep ? (
            <Button
              variant="contained"
              disabled={!form.userType}
              onClick={continueFromTypeStep}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              disabled={loading || loadingUser}
              onClick={saveForm}
            >
              {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
            </Button>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}
